import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import {
  ResponsiveModalHeaderComponent,
  ResponsiveModalContentComponent,
  ResponsiveModalActionsComponent,
} from 'app/shared/components/responsive-modal';

export interface IntegrationErrorJsonViewerData {
  title: string;
  json: unknown;
}

/**
 * Visor de JSON para la data enviada/recibida de un log de integración. Mismo
 * visor que usa la pantalla de Latencia (`ngx-json-viewer`), con dos mejoras:
 * botón para copiar el JSON completo al portapapeles y contenido con scroll
 * propio para payloads grandes.
 */
@Component({
  selector: 'app-integration-error-json-viewer-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    NgxJsonViewerModule,
    ResponsiveModalHeaderComponent,
    ResponsiveModalContentComponent,
    ResponsiveModalActionsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-responsive-modal-header
      [title]="data.title"
      icon="data_object"
      iconColor="slate"
    />

    <app-responsive-modal-content padding="p-3">
      <div class="max-w-full overflow-auto break-words">
        <ngx-json-viewer [json]="data.json" [expanded]="false"></ngx-json-viewer>
      </div>
    </app-responsive-modal-content>

    <app-responsive-modal-actions align="between">
      <button mat-stroked-button (click)="copyJson()">
        <mat-icon svgIcon="heroicons_outline:clipboard-document"></mat-icon>
        Copiar JSON
      </button>
      <button mat-raised-button color="primary" mat-dialog-close>Aceptar</button>
    </app-responsive-modal-actions>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
      }
    `,
  ],
})
export class IntegrationErrorJsonViewerModalComponent {
  readonly data: IntegrationErrorJsonViewerData = inject(MAT_DIALOG_DATA);
  private readonly handlerMessage = inject(HandlerMessageService);

  copyJson(): void {
    const text = JSON.stringify(this.data.json, null, 2);
    navigator.clipboard
      .writeText(text)
      .then(() => this.handlerMessage.success({ message: 'JSON copiado al portapapeles' }))
      .catch(() => this.handlerMessage.error({ message: 'No se pudo copiar el JSON' }));
  }
}
